import { FormEvent, useEffect } from "react"
import { useForm, usePage } from "@inertiajs/react"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Field, ErrorSummary } from "@/components/ui/form-field"
import { track } from "@/lib/analytics"
import type { PageProps } from "@/types/inertia"

// Shared SEO audit form used on both the focused /seo/audit page and embedded
// in the /seo marketing page. Both POST to /seo/audit and redirect to the
// token-keyed results page when the job is enqueued.
export function SeoAuditForm({
  source,
  ctaLabel = "Run my free audit",
}: {
  source: string
  ctaLabel?: string
}) {
  const { props } = usePage<PageProps>()
  const errors = props.errors ?? {}

  const form = useForm({
    url: "",
    email: "",
    business_name: "",
    nickname: "",
    referrer: "",
    utm_source: "",
  })

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    form.setData((data) => ({
      ...data,
      referrer: document.referrer || "",
      utm_source: params.get("utm_source") || "",
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const submit = (e: FormEvent) => {
    e.preventDefault()
    track("SEO Audit Requested")
    form.post("/seo/audit")
  }

  // Ids are suffixed so the form can appear on both /seo and /seo/audit without
  // colliding. The website field posts as `url` but the controller maps it onto
  // the model's `input_url`, so errors come back under that name — the summary
  // has to bridge the two to link anywhere real.
  const fieldId = (key: string) => `${key === "input_url" ? "url" : key}-${source}`

  return (
    <>
      <ErrorSummary errors={errors} subject="audit request" fieldId={fieldId} />

      <form onSubmit={submit} className="space-y-5" noValidate>
        <div aria-hidden="true" className="absolute left-[-9999px] top-[-9999px] h-0 w-0 overflow-hidden">
          <label htmlFor={`nickname-${source}`}>Leave this field empty</label>
          <input
            id={`nickname-${source}`}
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={form.data.nickname}
            onChange={(e) => form.setData("nickname", e.target.value)}
          />
        </div>

        <Field label="Your website" htmlFor={`url-${source}`} error={errors.input_url}>
          <Input
            id={`url-${source}`}
            placeholder="acme.com"
            inputMode="url"
            value={form.data.url}
            onChange={(e) => form.setData("url", e.target.value)}
          />
        </Field>
        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Email" htmlFor={`email-${source}`} error={errors.email}>
            <Input
              id={`email-${source}`}
              type="email"
              autoComplete="email"
              value={form.data.email}
              onChange={(e) => form.setData("email", e.target.value)}
            />
          </Field>
          <Field label="Business name" htmlFor={`business_name-${source}`} optional>
            <Input
              id={`business_name-${source}`}
              autoComplete="organization"
              value={form.data.business_name}
              onChange={(e) => form.setData("business_name", e.target.value)}
            />
          </Field>
        </div>
        <Button type="submit" size="lg" disabled={form.processing}>
          {form.processing ? "Starting…" : ctaLabel}
        </Button>
      </form>
    </>
  )
}
